import type { Locale } from "@zoya/i18n";
import { ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon as ArrowRight } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { CaretLeftIcon as CaretLeft } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon as CaretRight } from "@phosphor-icons/react/dist/csr/CaretRight";
import { PauseIcon as Pause } from "@phosphor-icons/react/dist/csr/Pause";
import { PlayIcon as Play } from "@phosphor-icons/react/dist/csr/Play";
import { useCallback, useEffect, useRef, useState } from "react";

export type AnnouncementReelItem = {
  id: string;
  kind: "announcement" | "delivery" | "product";
  label: string;
  title: string;
  description: string;
  image: { src: string; alt: string };
  href?: string;
  actionLabel?: string;
};

type AnnouncementReelProps = {
  ariaLabel: string;
  eyebrow: string;
  title: string;
  body: string;
  pauseLabel: string;
  resumeLabel: string;
  items: AnnouncementReelItem[];
  locale: Locale;
  navigate: (path: string) => void;
};

function ReelCard({
  item,
  locale,
  navigate,
  interactive,
  isActive,
}: {
  item: AnnouncementReelItem;
  locale: Locale;
  navigate: (path: string) => void;
  interactive: boolean;
  isActive?: boolean;
}) {
  const content = (
    <>
      <div className="announcement-card__image">
        {item.image.src ? (
          <img
            src={item.image.src}
            alt={interactive ? item.image.alt : ""}
            width="960"
            height="720"
            loading="lazy"
          />
        ) : (
          <div className="media-fallback" aria-hidden="true" />
        )}
      </div>
      <div className="announcement-card__copy">
        <span className="announcement-card__kind">{item.label}</span>
        <h3>{item.title}</h3>
        <p>{item.description}</p>
        {interactive && item.href && item.actionLabel ? (
          <span className="announcement-card__action">
            {item.actionLabel}
            {locale === "ar" ? (
              <ArrowLeft aria-hidden="true" />
            ) : (
              <ArrowRight aria-hidden="true" />
            )}
          </span>
        ) : null}
      </div>
    </>
  );

  if (!interactive || !item.href)
    return (
      <article
        className="announcement-card"
        data-kind={item.kind}
        data-active={isActive || undefined}
      >
        {content}
      </article>
    );

  return (
    <a
      className="announcement-card announcement-card--link"
      data-kind={item.kind}
      data-active={isActive || undefined}
      href={item.href}
      onClick={(event) => {
        if (!event.ctrlKey && !event.metaKey && event.button === 0) {
          event.preventDefault();
          navigate(item.href!);
        }
      }}
    >
      {content}
    </a>
  );
}

export function AnnouncementReel({
  ariaLabel,
  eyebrow,
  title,
  body,
  pauseLabel,
  resumeLabel,
  items,
  locale,
  navigate,
}: AnnouncementReelProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const isRtl = locale === "ar";
  const total = items.length;

  const goToNext = useCallback(() => {
    if (total === 0) return;
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const goToPrev = useCallback(() => {
    if (total === 0) return;
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  useEffect(() => {
    if (!isPlaying || total <= 1) return;
    const interval = setInterval(() => {
      goToNext();
    }, 4800);
    return () => clearInterval(interval);
  }, [isPlaying, total, goToNext]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (isRtl) goToNext();
      else goToPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      if (isRtl) goToPrev();
      else goToNext();
    }
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const touchEndX = event.changedTouches[0]?.clientX ?? 0;
    const deltaX = touchEndX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(deltaX) > 40) {
      if (deltaX > 0) {
        if (isRtl) goToNext();
        else goToPrev();
      } else {
        if (isRtl) goToPrev();
        else goToNext();
      }
    }
  };

  return (
    <section
      className="announcement-reel announcement-reel--circular"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="announcement-reel__heading reveal-item">
        <p className="eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{body}</p>

        {/* Circular Control Bar */}
        <div className="announcement-reel__controls" role="toolbar">
          <button
            type="button"
            className="announcement-reel__nav-btn"
            aria-label={isRtl ? "Next announcement" : "Previous announcement"}
            onClick={isRtl ? goToNext : goToPrev}
          >
            {isRtl ? (
              <CaretRight aria-hidden="true" />
            ) : (
              <CaretLeft aria-hidden="true" />
            )}
          </button>

          <div
            className="announcement-reel__dots"
            role="tablist"
            aria-label="Announcement slides"
          >
            {items.map((item, index) => {
              const isActive = index === currentIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  className={`announcement-reel__dot ${isActive ? "announcement-reel__dot--active" : ""}`}
                  aria-selected={isActive}
                  aria-label={`${item.title} (${index + 1} of ${total})`}
                  onClick={() => goToSlide(index)}
                />
              );
            })}
          </div>

          <button
            type="button"
            className="announcement-reel__nav-btn"
            aria-label={isRtl ? "Previous announcement" : "Next announcement"}
            onClick={isRtl ? goToPrev : goToNext}
          >
            {isRtl ? (
              <CaretLeft aria-hidden="true" />
            ) : (
              <CaretRight aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            className="announcement-reel__play-btn"
            aria-pressed={!isPlaying}
            aria-label={isPlaying ? pauseLabel : resumeLabel}
            onClick={() => setIsPlaying((prev) => !prev)}
            title={isPlaying ? pauseLabel : resumeLabel}
          >
            {isPlaying ? (
              <Pause aria-hidden="true" />
            ) : (
              <Play aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <div
        className="announcement-reel__viewport"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="announcement-reel__track announcement-reel__track--carousel"
          data-direction={locale}
          style={
            {
              "--current-index": currentIndex,
              "--total-items": total,
              animationPlayState: isPlaying ? "running" : "paused",
            } as React.CSSProperties
          }
        >
          <ul className="announcement-reel__set">
            {items.map((item, index) => (
              <li key={item.id}>
                <ReelCard
                  item={item}
                  locale={locale}
                  navigate={navigate}
                  interactive
                  isActive={index === currentIndex}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
